"use client";

import React, { useState } from "react";

export interface CommunityPaymentModelProps {
  title?: string;
  subtitle?: string;
  description?: string;
  features?: Array<{
    title: string;
    description: string;
  }>;
}

export function CommunityPaymentModel({
  title = "A Community That Cares",
  subtitle = "",
  description = "We built a different kind of therapy practice. One where your rate changes with your life.",
  features = [
    {
      title: "How It Works",
      description: "When you choose to pay more, you're directly helping someone else access therapy at a lower rate. Together, we balance out so everyone gets the support they need."
    },
    {
      title: "Change Your Rate Anytime",
      description: "Lost your job Monday? Lower your rate Tuesday. No forms, no proof, no questions. Got a raise? Slide it up when you're ready. It takes 30 seconds in your account."
    },
    {
      title: "Why This Works",
      description: "<strong>Traditional therapy:</strong> can't afford it? Too bad.<br><br><strong>Our way:</strong> 38,000+ Canadians staying in therapy through job losses, mat leaves, and tough times. Because mental health can't wait for perfect finances."
    },
    {
      title: "The Privacy Promise",
      description: "Your rate is between you and us - that's it. Your therapist focuses on what matters: you. No awkward money conversations, no judgment, just genuine care."
    }
  ]
}: CommunityPaymentModelProps) {
  const [selectedTherapist, setSelectedTherapist] = useState<'intern' | 'associate'>('intern');
  const [selectedRate, setSelectedRate] = useState(65);
  
  const therapistRanges = {
    intern: { min: 45, max: 115 },
    associate: { min: 125, max: 200 }
  };
  
  const currentRange = therapistRanges[selectedTherapist];
  const ratePercentage = ((selectedRate - currentRange.min) / (currentRange.max - currentRange.min)) * 100;
  
  const getCommunityRole = () => {
    const percentage = ratePercentage;
    if (percentage < 50) return "Community Member";
    return "Community Builder";
  };
  
  const getContribution = () => {
    const midpoint = (currentRange.min + currentRange.max) / 2;
    if (selectedRate < midpoint) {
      return "Taking care of yourself matters";
    } else if (selectedRate > midpoint) {
      return "Someone's therapy continues because of you";
    } else {
      return "Every rate helps our community thrive";
    }
  };
  
  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-gray-900 mb-2">
            {title}
          </h2>
          <p className="text-3xl font-light text-gray-900 mb-6">
            {subtitle}
          </p>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {description}
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
          {/* Left side - Interactive visual */}
          <div className="order-2 md:order-1">
            <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-3xl p-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">
                You Set The Terms
              </h3>
              <p className="text-gray-600 mb-8 text-center">
                Your choice powers care for yourself and others
              </p>
              
              {/* Therapist type selection */}
              <div className="mb-8">
                <p className="text-sm font-medium text-gray-700 mb-4">Choose your therapist level:</p>
                <div className="grid grid-cols-2 gap-4">
                  <button 
                    onClick={() => {
                      setSelectedTherapist('intern');
                      setSelectedRate(therapistRanges.intern.min + 20);
                    }}
                    className={`group relative p-6 bg-white rounded-2xl shadow-sm transition-all transform hover:-translate-y-0.5 ${
                      selectedTherapist === 'intern' ? 'ring-2 ring-primary-500 shadow-md' : 'hover:shadow-md'
                    }`}
                  >
                    <p className="font-bold text-lg text-gray-900 mb-2">Intern Therapist</p>
                    <p className="text-primary-600 font-semibold">$45 - $115</p>
                  </button>
                  <button 
                    onClick={() => {
                      setSelectedTherapist('associate');
                      setSelectedRate(therapistRanges.associate.min + 30);
                    }}
                    className={`group relative p-6 bg-white rounded-2xl shadow-sm transition-all transform hover:-translate-y-0.5 ${
                      selectedTherapist === 'associate' ? 'ring-2 ring-primary-500 shadow-md' : 'hover:shadow-md'
                    }`}
                  >
                    <p className="font-bold text-lg text-gray-900 mb-2">Associate Therapist</p>
                    <p className="text-primary-600 font-semibold">$125 - $200</p>
                  </button>
                </div>
              </div>
              
              {/* Community slider visualization */}
              <div className="mb-8">
                <p className="text-sm font-medium text-gray-700 mb-4">Select your session rate:</p>
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <div className="mb-2">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">${currentRange.min}</span>
                      <span className="text-2xl font-bold text-primary-600">${selectedRate}</span>
                      <span className="text-gray-600">${currentRange.max}</span>
                    </div>
                  </div>
                  <div className="relative mb-4">
                    <input
                      type="range"
                      min={currentRange.min}
                      max={currentRange.max}
                      value={selectedRate}
                      onChange={(e) => setSelectedRate(parseInt(e.target.value))}
                      className="w-full h-3 bg-gradient-to-r from-primary-100 via-primary-300 to-primary-500 rounded-full appearance-none cursor-pointer slider"
                      style={{
                        background: `linear-gradient(to right, #DBEAFE 0%, #60A5FA ${ratePercentage}%, #E5E7EB ${ratePercentage}%, #E5E7EB 100%)`
                      }}
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-primary-600">{getCommunityRole()}</p>
                    <p className="text-xs text-gray-600 mt-1">{getContribution()}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white/70 backdrop-blur rounded-2xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Your Rate: 100% Confidential</p>
                    <p className="text-xs text-gray-600">Your therapist never sees what you pay</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right side - Features */}
          <div className="order-1 md:order-2 space-y-8">
            {features.map((feature, index) => {
              const icons = [
                // Flexible Pricing
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />,
                // Community
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />,
                // Privacy
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />,
                // Barriers
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              ];
              
              return (
                <div key={index} className="group flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        {icons[index]}
                      </svg>
                    </div>
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p 
                      className="text-gray-600 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: feature.description }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      <style>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 24px;
          height: 24px;
          background: white;
          border: 4px solid #0e7490;
          border-radius: 50%;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          transition: transform 0.2s;
        }
        
        .slider::-webkit-slider-thumb:hover {
          transform: scale(1.1);
        }
        
        .slider::-moz-range-thumb {
          width: 24px;
          height: 24px;
          background: white;
          border: 4px solid #0e7490;
          border-radius: 50%;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          transition: transform 0.2s;
        }
        
        .slider::-moz-range-thumb:hover {
          transform: scale(1.1);
        }
      `}</style>
    </section>
  );
}